import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { HandHeart, Flower } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Donate() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [processing, setProcessing] = useState(false);

  const presetAmounts = [10, 25, 50, 100];

  const handleDonate = async (donationAmount) => {
    if (!donationAmount || donationAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setProcessing(true);
      const response = await axios.post(
        `${API}/donations/checkout`,
        {
          amount: parseFloat(donationAmount),
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Donation error:', error);
      toast.error('Failed to process donation. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white/70 backdrop-blur-lg border-b border-white/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Flower size={32} weight="fill" className="text-primary" />
            <span className="text-2xl font-heading font-bold">Bloom & Bond</span>
          </div>
          <Button 
            data-testid="back-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            size="sm"
          >
            ← Back
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <HandHeart size={64} weight="fill" className="text-accent mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">Support Our Community</h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Your donations help us maintain our platform, organize events, and spread kindness. Every contribution makes a difference!
            </p>
          </div>

          <div className="bg-white/80 backdrop-blur-md border border-white/20 shadow-lg rounded-3xl p-8 md:p-12">
            <h2 className="text-2xl font-heading font-semibold mb-6">Choose Your Donation Amount</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {presetAmounts.map((preset) => (
                <motion.button
                  key={preset}
                  data-testid={`preset-amount-${preset}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleDonate(preset)}
                  disabled={processing}
                  className="bg-white border-2 border-primary/30 hover:border-primary hover:bg-primary/5 rounded-2xl p-6 transition-all duration-300 disabled:opacity-50"
                >
                  <div className="text-3xl font-heading font-bold text-primary">${preset}</div>
                </motion.button>
              ))}
            </div>

            <div className="space-y-4">
              <div className="text-center text-muted-foreground font-medium">Or enter a custom amount</div>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-muted-foreground">$</span>
                  <Input
                    data-testid="custom-amount-input"
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={processing}
                    className="pl-10 text-2xl font-bold rounded-xl h-14"
                  />
                </div>
                <Button
                  data-testid="custom-donate-button"
                  onClick={() => handleDonate(amount)}
                  disabled={processing || !amount}
                  className="rounded-xl bg-primary hover:bg-primary-hover font-bold px-8 h-14"
                >
                  {processing ? 'Processing...' : 'Donate'}
                </Button>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-br from-[#FF9A9E] to-[#FECFEF] rounded-2xl p-6 text-white">
              <h3 className="font-heading font-semibold mb-2">Your Impact</h3>
              <p className="text-white/90 text-sm">
                Donations help us host events, maintain the platform, support members in need, and keep our community thriving. Thank you for your generosity!
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}